import React, { useState, useEffect } from 'react';
import {
  Menu,
  MenuItem,
  Divider,
  ListItemIcon,
  ListItemText,
  Dialog,
  DialogTitle,
  DialogContent,
  Typography,
  Box,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  List,
  ListItem,
  ListItemButton,
  Chip,
  CircularProgress,
  Rating,
  Grid,
  Paper,
  Button,
  TextField,
  DialogActions,
  IconButton,
  Tooltip,
  Snackbar,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  InputAdornment
} from '@mui/material';

import RestartAltIcon from '@mui/icons-material/RestartAlt';
import HistoryIcon from '@mui/icons-material/History';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import StarIcon from '@mui/icons-material/Star';
import WorkIcon from '@mui/icons-material/Work';
import PersonIcon from '@mui/icons-material/Person';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import { alpha, useTheme } from '@mui/material/styles';
import { styled } from '@mui/material/styles';
import { bg, border, gradient } from '../../utils/darkModeAdapter';
import SupervisorAccountIcon from '@mui/icons-material/SupervisorAccount';
import BlockIcon from '@mui/icons-material/Block';
import DeleteIcon from '@mui/icons-material/Delete';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import ManageAccountsIcon from '@mui/icons-material/ManageAccounts';
import EditIcon from '@mui/icons-material/Edit';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import SearchIcon from '@mui/icons-material/Search';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import CheckIcon from '@mui/icons-material/Check';
import axios from 'axios';
import { useAuth } from '../../hooks/useAuth';
import { motion, AnimatePresence } from 'framer-motion';

/**
 * AssistMenu コンポーネント:
 *  Props:
 *    - anchorEl: メニューのアンカー要素
 *    - open: メニュー表示フラグ (boolean)
 *    - onClose: メニューを閉じるときのハンドラ
 *    - onRestart: 「初めからやり直す」ボタン押下時のハンドラ
 *    - onShowHistory: 「過去の出力結果を確認」ボタン押下時のハンドラ
 *    - loading: ローディング状態 (boolean)
 *    - error: エラーメッセージ (string | null)
 *    - onStartFromPast: 過去のチェックシートから面接を開始するハンドラ
 */

// 面接結果に応じたチップを返すコンポーネント
const InterviewResultChip = ({ checkSheet }) => {

  // 採用/不採用の判定
  if (checkSheet.decision_status === 'hired') {
    return (
      <StatusChip
        status="hired"
        icon={<WorkIcon />}
        label={checkSheet.is_final_stage ? "最終面接で採用" : "採用決定"}
        size="small"
      />
    );
  }

  if (checkSheet.decision_status === 'rejected') {
    return (
      <StatusChip
        status="rejected"
        icon={<BlockIcon />}
        label={checkSheet.is_final_stage ? "最終面接で不採用" : "不採用"}
        size="small"
      />
    );
  }
  
  // 次の面接に進んだかどうか
  if (checkSheet.next_interview_created) {
    return (
      <StatusChip
        status="next"
        icon={<PlayArrowIcon />}
        label="次の面接へ進行"
        size="small"
      />
    );
  }

  // 評価完了
  if (checkSheet.is_completed) {
    return (
      <StatusChip
        status="completed"
        icon={<CheckCircleIcon />}
        label="評価完了"
        size="small"
      />
    );
  }

  return null;
};

// ステータスチップのスタイル定義を更新
const StatusChip = styled(Chip)(({ theme, status }) => {
  const colors = {
    completed: {
      bg: alpha(theme.palette.success.main, 0.1),
      color: theme.palette.success.main,
      border: theme.palette.success.main
    },
    hired: {
      bg: alpha(theme.palette.success.main, 0.15),
      color: theme.palette.success.dark,
      border: theme.palette.success.dark
    },
    rejected: {
      bg: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: theme.palette.error.main
    },
    next: {
      bg: alpha(theme.palette.info.main, 0.1),
      color: theme.palette.info.main,
      border: theme.palette.info.main
    },
    in_progress: {
      bg: alpha(theme.palette.warning.main, 0.1),
      color: theme.palette.warning.main,
      border: theme.palette.warning.main
    },
    first: {
      bg: alpha(theme.palette.primary.main, 0.1),
      color: theme.palette.primary.main,
      border: theme.palette.primary.main
    },
    second: {
      bg: alpha(theme.palette.secondary.main, 0.1),
      color: theme.palette.secondary.main,
      border: theme.palette.secondary.main
    },
    final: {
      bg: alpha(theme.palette.error.main, 0.1),
      color: theme.palette.error.main,
      border: theme.palette.error.main
    }
  };

  return {
    backgroundColor: colors[status]?.bg || alpha(theme.palette.grey[500], 0.1),
    color: colors[status]?.color || theme.palette.grey[500],
    borderColor: colors[status]?.border || theme.palette.grey[500],
    borderWidth: 1,
    borderStyle: 'solid',
    '& .MuiChip-icon': {
      color: 'inherit'
    }
  };
});

// 面接ステージに応じたチップを返すコンポーネント
const InterviewStageChip = ({ stage, stageOrder, totalStages, isFinal }) => {
  let status = 'in_progress';
  let label = stage;
  
  if (stageOrder === 1) status = 'first';
  else if (isFinal) status = 'final';
  else if (stageOrder === 2) status = 'second';
  
  return (
    <StatusChip
      status={status}
      size="small"
      label={`${label} (${stageOrder}/${totalStages})`}
    />
  );
};

// チェックシートの状態に応じたチップを表示するコンポーネントを更新
const StatusChips = ({ checkSheet }) => (
  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
    <InterviewStageChip
      stage={checkSheet.interview_stage}
      stageOrder={checkSheet.interview_stage_order}
      totalStages={checkSheet.total_stages}
      isFinal={checkSheet.is_final_stage}
    />
    <InterviewResultChip checkSheet={checkSheet} />
    {checkSheet.evaluation && (
      <StatusChip 
        status="completed"
        icon={<StarIcon />}
        label={`評価: ${checkSheet.evaluation.overall_score}/100`}
        size="small"
      />
    )}
  </Box>
);

// 候補者の最終結果を表示するコンポーネント
const CandidateFinalResult = ({ checkSheets }) => {
  // 最新のチェックシートを取得
  const latestSheet = checkSheets[0];
  if (!latestSheet) return null;

  // 最終結果を判定
  if (latestSheet.decision_status === 'hired') {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: alpha('#4caf50', 0.1),
        px: 1,
        py: 0.5,
        borderRadius: 1
      }}>
        <WorkIcon sx={{ color: 'success.main', fontSize: 20 }} />
        <Typography variant="body2" color="success.main" fontWeight="medium">
          採用決定
        </Typography>
      </Box>
    );
  }

  if (latestSheet.decision_status === 'rejected') {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: alpha('#f44336', 0.1),
        px: 1,
        py: 0.5,
        borderRadius: 1
      }}>
        <BlockIcon sx={{ color: 'error.main', fontSize: 20 }} />
        <Typography variant="body2" color="error.main" fontWeight="medium">
          不採用
        </Typography>
      </Box>
    );
  }

  if (latestSheet.next_interview_created) {
    return (
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        gap: 1,
        bgcolor: alpha('#2196f3', 0.1),
        px: 1,
        py: 0.5,
        borderRadius: 1
      }}>
        <PlayArrowIcon sx={{ color: 'info.main', fontSize: 20 }} />
        <Typography variant="body2" color="info.main" fontWeight="medium">
          次の面接へ進行中
        </Typography>
      </Box>
    );
  }

  return null;
};

export default function AssistMenu({
  anchorEl,
  open,
  onClose,
  onRestart,
  onShowHistory,
  loading: parentLoading,
  error: parentError,
  onStartFromPast,
  onDeleteClick,
  deletedCheckSheetId
}) {
  const { user } = useAuth();
  const theme = useTheme();

  // 履歴ダイアログの状態管理
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [historyData, setHistoryData] = useState(null);
  const [error, setError] = useState(null);
  const [selectedCheckSheet, setSelectedCheckSheet] = useState(null);
  const [historySearchQuery, setHistorySearchQuery] = useState('');

  // 過去の一次面接チェックシート一覧用のダイアログ状態
  const [pastFirstInterviewsDialogOpen, setPastFirstInterviewsDialogOpen] = useState(false);
  const [pastFirstInterviews, setPastFirstInterviews] = useState([]);
  const [pastInterviewsLoading, setPastInterviewsLoading] = useState(false);
  const [pastInterviewsError, setPastInterviewsError] = useState(null);
  const [pastInterviewsSearchQuery, setPastInterviewsSearchQuery] = useState('');

  // 子アカウント管理の状態
  const [subAccountDialogOpen, setSubAccountDialogOpen] = useState(false);
  const [subAccountCreateDialogOpen, setSubAccountCreateDialogOpen] = useState(false);
  const [subAccounts, setSubAccounts] = useState([]);
  const [subAccountLoading, setSubAccountLoading] = useState(false);
  const [subAccountError, setSubAccountError] = useState(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState('success');
  
  // 新規子アカウント作成フォーム
  const [newSubAccount, setNewSubAccount] = useState({
    email: '',
    password: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  
  // 編集中の子アカウント
  const [editingSubAccount, setEditingSubAccount] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);

  // 利用可能なチェックシート一覧の状態
  const [availableCheckSheetsDialogOpen, setAvailableCheckSheetsDialogOpen] = useState(false);
  const [availableCheckSheets, setAvailableCheckSheets] = useState({});
  const [availableCheckSheetsLoading, setAvailableCheckSheetsLoading] = useState(false);
  const [availableCheckSheetsError, setAvailableCheckSheetsError] = useState(null);
  const [availableCheckSheetsSearchQuery, setAvailableCheckSheetsSearchQuery] = useState('');

  // メニュー項目の共通スタイル
  const menuItemStyle = (color = '#fc67fa') => ({
    '&:hover': {
      backgroundColor: alpha(color, 0.1),
    },
    padding: '12px 16px',
    margin: '2px 0',
    borderRadius: '8px',
    '& .MuiListItemIcon-root': {
      color: color,
      minWidth: '40px',
    },
    '& .MuiListItemText-primary': {
      fontWeight: 'bold',
      fontSize: '0.95rem',
    }
  });

  // 削除されたチェックシートIDを監視して全てのダイアログの状態から除外
  useEffect(() => {
    if (!deletedCheckSheetId) return;

    // 過去の一次面接チェックシート一覧から削除
    setPastFirstInterviews(prev => {
      const updated = {};
      Object.entries(prev).forEach(([position, checkSheets]) => {
        const filtered = checkSheets.filter(sheet => sheet.id !== deletedCheckSheetId);
        if (filtered.length > 0) {
          updated[position] = filtered;
        }
      });
      return updated;
    });

    // 過去の面接チェックシート履歴から削除
    setHistoryData(prev => {
      if (!prev) return prev;
      const updated = {};
      Object.entries(prev).forEach(([candidateName, checkSheets]) => {
        const filtered = checkSheets.filter(sheet => sheet.id !== deletedCheckSheetId);
        if (filtered.length > 0) {
          updated[candidateName] = filtered;
        }
      });
      return updated;
    });

    // 面接未実施チェックシート一覧から削除
    setAvailableCheckSheets(prev => {
      const updated = {};
      Object.entries(prev).forEach(([candidateKey, checkSheets]) => {
        const filtered = checkSheets.filter(sheet => sheet.id !== deletedCheckSheetId);
        if (filtered.length > 0) {
          updated[candidateKey] = filtered;
        }
      });
      return updated;
    });
  }, [deletedCheckSheetId]);

  // 履歴ダイアログを開く
  const handleOpenHistoryDialog = async () => {
    setHistoryDialogOpen(true);
    setLoading(true);
    setError(null);
    setSelectedCheckSheet(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets?status=completed`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const data = response.data;

      // データを応募者名と職種でグループ化
      const groupedData = data.reduce((acc, item) => {
        // 応募者情報から名前を取得（優先順位: candidate_info > candidate_name）
        const candidateName = 
          (item.candidate_info && item.candidate_info.name) || 
          item.candidate_name || 
          '未設定の応募者';
        
        const position = item.position || '職種未設定';
        const key = `${candidateName} - ${position}`;

        if (!acc[key]) {
          acc[key] = [];
        }
        
        acc[key].push(item);
        return acc;
      }, {});

      // 各グループ内で面接段階順にソート
      Object.keys(groupedData).forEach(key => {
        groupedData[key].sort((a, b) => {
          const orderA = a.interview_stage_order || 1;
          const orderB = b.interview_stage_order || 1;
          return orderA - orderB;
        });
      });

      setHistoryData(groupedData);
    } catch (err) {
      console.error('Error fetching history:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  // 履歴ダイアログを閉じる
  const handleCloseHistoryDialog = () => {
    setHistoryDialogOpen(false);
    setHistoryData(null);
    setError(null);
    setSelectedCheckSheet(null);
  };

  // チェックシートを選択
  const handleSelectCheckSheet = (checkSheet) => {
    if (checkSheet.is_completed && checkSheet.evaluation) {
      setSelectedCheckSheet(checkSheet);
    } else {
      handleContinueInterview(checkSheet);
    }
  };

  // 選択したチェックシートで面接を再開
  const handleContinueInterview = (checkSheet) => {
    if (checkSheet) {
      try {
        // チェックシートIDのみを渡す
        onShowHistory(checkSheet.id);
        handleCloseHistoryDialog();
        onClose();
      } catch (err) {
        console.error('Error continuing interview:', err);
        setError(err.message);
      }
    }
  };

  // 過去の一次面接チェックシートを取得する関数
  const fetchPastFirstInterviews = async () => {
    setPastInterviewsLoading(true);
    setPastInterviewsError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets`,
        {
          headers: {
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const data = response.data;
      
      // 一次面接のチェックシートのみをフィルタリング
      const firstInterviews = data.filter(sheet => 
        sheet.interview_stage === '1次面接' || 
        sheet.interview_stage_order === 1
      );

      // データを職種でグループ化
      const groupedData = firstInterviews.reduce((acc, item) => {
        const position = item.position || '未設定の職種';

        if (!acc[position]) {
          acc[position] = [];
        }
        acc[position].push(item);
        return acc;
      }, {});

      setPastFirstInterviews(groupedData);
    } catch (err) {
      console.error('Error fetching past first interviews:', err);
      setPastInterviewsError(err.message);
    } finally {
      setPastInterviewsLoading(false);
    }
  };

  // 過去の一次面接チェックシートを選択したときの処理
  const handleSelectPastInterview = (checkSheet) => {
    if (onStartFromPast) {
      onStartFromPast(checkSheet.id);
      handleClosePastInterviewsDialog();
      onClose();
    }
  };

  // 過去の一次面接ダイアログを開く
  const handleOpenPastInterviewsDialog = () => {
    setPastFirstInterviewsDialogOpen(true);
    fetchPastFirstInterviews();
  };

  // 過去の一次面接ダイアログを閉じる
  const handleClosePastInterviewsDialog = () => {
    setPastFirstInterviewsDialogOpen(false);
    setPastFirstInterviews([]);
    setPastInterviewsError(null);
  };

  // 子アカウント関連の関数
  const fetchSubAccounts = async () => {
    setSubAccountLoading(true);
    setSubAccountError(null);
    
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/users/sub-accounts`, {
        withCredentials: true,
      });
      setSubAccounts(response.data);
    } catch (error) {
      setSubAccountError(error.response?.data?.detail || '子アカウントの取得に失敗しました');
    } finally {
      setSubAccountLoading(false);
    }
  };

  const handleCreateSubAccount = async () => {
    if (!newSubAccount.email || !newSubAccount.password) {
      setSnackbarMessage('メールアドレスとパスワードを入力してください');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
      return;
    }

    try {
      await axios.post(`${process.env.REACT_APP_API_URL}/users/sub-accounts`, {
        ...newSubAccount,
        role: 'interview_only'
      }, {
        withCredentials: true,
      });
      
      setSnackbarMessage('子アカウントを作成しました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      setSubAccountCreateDialogOpen(false);
      setNewSubAccount({ email: '', password: '' });
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || '子アカウントの作成に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleDeleteSubAccount = async (subAccountId) => {
    if (!window.confirm('このアカウントを削除しますか？')) return;

    try {
      await axios.delete(`${process.env.REACT_APP_API_URL}/users/sub-accounts/${subAccountId}`, {
        withCredentials: true,
      });
      
      setSnackbarMessage('子アカウントを削除しました');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || '子アカウントの削除に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleToggleSubAccountLock = async (subAccount) => {
    try {
      await axios.put(`${process.env.REACT_APP_API_URL}/users/sub-accounts/${subAccount.id}`, {
        locked: !subAccount.locked
      }, {
        withCredentials: true,
      });
      
      setSnackbarMessage(`アカウントを${subAccount.locked ? 'ロック解除' : 'ロック'}しました`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      fetchSubAccounts();
    } catch (error) {
      setSnackbarMessage(error.response?.data?.detail || 'アカウントの状態変更に失敗しました');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    }
  };

  const handleOpenSubAccountDialog = () => {
    setSubAccountDialogOpen(true);
    fetchSubAccounts();
  };

  const handleCloseSubAccountDialog = () => {
    setSubAccountDialogOpen(false);
    setSubAccountCreateDialogOpen(false);
    setEditDialogOpen(false);
    setEditingSubAccount(null);
    setNewSubAccount({ email: '', password: '' });
  };

  // 利用可能なチェックシート一覧を取得する関数
  const fetchAvailableCheckSheets = async () => {
    setAvailableCheckSheetsLoading(true);
    setAvailableCheckSheetsError(null);

    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/interview_assistant/check-sheets?status=in_progress`,
        {
          headers: {
            
            'Content-Type': 'application/json'
          },
          withCredentials: true
        }
      );

      const data = response.data;
      
      // 面接未実施のもののみをフィルタリング
      const uncompletedCheckSheets = data.filter(checkSheet => 
        !checkSheet.is_completed && 
        (!checkSheet.evaluation || !checkSheet.evaluation.overall_score)
      );
      
      // 候補者ごとにグループ化
      const groupedCheckSheets = uncompletedCheckSheets.reduce((acc, checkSheet) => {
        // 候補者名取得の優先順位を調整
        const candidateName = (checkSheet.candidate_info && checkSheet.candidate_info.name) || 
                            checkSheet.candidate_name || 
                            '候補者名未設定';
        
        const position = checkSheet.position || '職種未設定';
        const key = `${candidateName} - ${position}`;
        
        if (!acc[key]) {
          acc[key] = [];
        }
        acc[key].push(checkSheet);
        return acc;
      }, {});

      // 各グループ内で面接段階順にソート（1次、2次、3次...）
      Object.keys(groupedCheckSheets).forEach(key => {
        groupedCheckSheets[key].sort((a, b) => {
          const orderA = a.interview_stage_order || 1;
          const orderB = b.interview_stage_order || 1;
          return orderA - orderB;
        });
      });
      
      setAvailableCheckSheets(groupedCheckSheets);
      
    } catch (error) {
      console.error('面接未実施チェックシート一覧の取得に失敗しました:', error);
      setAvailableCheckSheetsError(error.message);
    } finally {
      setAvailableCheckSheetsLoading(false);
    }
  };

  // 面接未実施チェックシートダイアログを開く
  const handleOpenAvailableCheckSheetsDialog = () => {
    setAvailableCheckSheetsDialogOpen(true);
    fetchAvailableCheckSheets();
  };

  // 面接未実施チェックシートダイアログを閉じる
  const handleCloseAvailableCheckSheetsDialog = () => {
    setAvailableCheckSheetsDialogOpen(false);
    setAvailableCheckSheets({});
    setAvailableCheckSheetsError(null);
  };

  // 面接未実施チェックシートから選択したときの処理
  const handleSelectAvailableCheckSheet = (checkSheet) => {
    if (onShowHistory) {
      onShowHistory(checkSheet.id);
      handleCloseAvailableCheckSheetsDialog();
      onClose();
    }
  };

  // メニューが閉じられたときにダイアログも閉じる
  useEffect(() => {
    if (!open) {
      handleCloseHistoryDialog();
      handleClosePastInterviewsDialog();
      handleCloseSubAccountDialog();
      handleCloseAvailableCheckSheetsDialog();
    }
  }, [open]);

  return (
    <>
      <Menu
        anchorEl={anchorEl}
        open={open}
        onClose={onClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
        transformOrigin={{ vertical: 'top', horizontal: 'center' }}
        sx={{
          '& .MuiPaper-root': {
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            minWidth: 220,
            padding: '8px',
            background: gradient(theme).menu,
          },
        }}
      >
        {/* 新規作成関連メニューは子アカウントでは非表示 */}
        {user?.role !== 'interview_only' && (
          <>
            <MenuItem onClick={onRestart} sx={menuItemStyle('#f44336')}>
              <ListItemIcon>
                <RestartAltIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>新しい面接を作成（最初から）</ListItemText>
            </MenuItem>

            <MenuItem onClick={handleOpenPastInterviewsDialog} sx={menuItemStyle('#4caf50')}>
              <ListItemIcon>
                <PlayArrowIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>過去のチェックシートから開始</ListItemText>
            </MenuItem>

            <Divider sx={{ my: 1, opacity: 0.6 }} />
          </>
        )}

        <MenuItem onClick={handleOpenHistoryDialog} sx={menuItemStyle('#48c6ef')}>
          <ListItemIcon>
            <HistoryIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>過去のチェックシートを確認</ListItemText>
        </MenuItem>

        <MenuItem onClick={handleOpenAvailableCheckSheetsDialog} sx={menuItemStyle('#ff9800')}>
          <ListItemIcon>
            <PersonIcon fontSize="small" />
          </ListItemIcon>
          <ListItemText>面接未実施チェックシート一覧</ListItemText>
        </MenuItem>

        {/* 子アカウント管理メニュー（generalとadminロールのみ） */}
        {user?.role && ['admin', 'general'].includes(user.role) && (
          <>
            <Divider sx={{ my: 1, opacity: 0.6 }} />
            <MenuItem onClick={handleOpenSubAccountDialog} sx={menuItemStyle('#9c27b0')}>
              <ListItemIcon>
                <ManageAccountsIcon fontSize="small" />
              </ListItemIcon>
              <ListItemText>子アカウント管理</ListItemText>
            </MenuItem>
          </>
        )}
      </Menu>

      {/* 過去の一次面接チェックシート一覧ダイアログ */}
      <Dialog
        open={pastFirstInterviewsDialogOpen}
        onClose={handleClosePastInterviewsDialog}
        maxWidth="md"
        fullWidth
      >
        {/* ヘッダー */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
            color: 'white',
            px: 3,
            py: 3
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            過去の一次面接チェックシート
          </Typography>

          {/* 検索バー */}
          <TextField
            fullWidth
            placeholder="職種、候補者名、キーワードで検索..."
            size="small"
            value={pastInterviewsSearchQuery}
            onChange={(e) => setPastInterviewsSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.25)
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.8),
                  opacity: 1
                },
                '& input': {
                  color: 'white'
                }
              }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle, position: 'relative' }}>
          {pastInterviewsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : pastInterviewsError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {pastInterviewsError}
            </Typography>
          ) : Object.keys(pastFirstInterviews).length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* サマリー情報 */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  mb: 3,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                  borderRadius: 3,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                }}
              >
                <PlayArrowIcon sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    一次面接チェックシートサマリー
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    全 {Object.keys(pastFirstInterviews).length} 職種 • 一次面接のチェックシートのみ表示
                  </Typography>
                </Box>
              </Box>

              {/* Timeline コンテナ */}
              <Box sx={{ position: 'relative', pl: 6 }}>
                {/* Timeline 垂直線 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 23,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: 'linear-gradient(180deg, #9C27B0 0%, #BA68C8 100%)',
                    borderRadius: 2,
                    opacity: 0.3
                  }}
                />

                {/* Timeline アイテム */}
                <AnimatePresence>
                  {Object.entries(pastFirstInterviews)
                    .filter(([position, checkSheets]) => {
                      if (!pastInterviewsSearchQuery.trim()) return true;
                      const query = pastInterviewsSearchQuery.toLowerCase();
                      const posMatch = position.toLowerCase().includes(query);
                      const sheetMatch = checkSheets.some(sheet =>
                        (sheet.candidate_name || '').toLowerCase().includes(query) ||
                        (sheet.position || '').toLowerCase().includes(query)
                      );
                      return posMatch || sheetMatch;
                    })
                    .map(([position, checkSheets], groupIndex) => (
                      <Box
                        key={position}
                        component={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{
                          duration: 0.4,
                          delay: groupIndex * 0.1,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                        sx={{ position: 'relative', mb: 3 }}
                      >
                        {/* Timeline アイコン */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -35,
                            top: 8,
                            width: 46,
                            height: 46,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)',
                            zIndex: 1,
                            border: `3px solid ${border(theme).light}`
                          }}
                        >
                          <WorkIcon fontSize="small" />
                        </Box>

                        {/* Timeline カード */}
                        <Box
                          component={motion.div}
                          whileHover={{
                            y: -4,
                            boxShadow: '0 8px 24px rgba(156, 39, 176, 0.2)'
                          }}
                          transition={{ duration: 0.2 }}
                          sx={{
                            p: 3,
                            backgroundColor: bg(theme).card,
                            borderRadius: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: `1px solid ${border(theme).default}`
                          }}
                        >
                          {/* カードヘッダー */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                                {position}
                              </Typography>
                              <Chip
                                size="small"
                                label={`${checkSheets.length}件`}
                                sx={{
                                  background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                                  color: 'white',
                                  fontWeight: 600
                                }}
                              />
                            </Box>
                          </Box>

                          {/* チェックシートリスト */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {checkSheets.map((checkSheet) => (
                              <Box
                                key={checkSheet.id}
                                component={motion.div}
                                whileHover={{ x: 4 }}
                                transition={{ duration: 0.2 }}
                                onClick={() => handleSelectPastInterview(checkSheet)}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #f3e5f7 0%, #fce4ec 100%)',
                                  border: '1px solid #e1bee7',
                                  cursor: 'pointer',
                                  opacity: checkSheet.decision_status === 'rejected' ? 0.7 : 1,
                                  '&:hover': {
                                    border: '1px solid #9C27B0',
                                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
                                      {checkSheet.candidate_name || '候補者未設定'}
                                    </Typography>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, color: 'text.secondary' }}>
                                      <AccessTimeIcon sx={{ fontSize: 14 }} />
                                      <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                        {new Date(checkSheet.interview_date).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                                      </Typography>
                                    </Box>
                                  </Box>
                                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                                    <StatusChips checkSheet={checkSheet} />
                                  </Box>
                                  {onDeleteClick && (
                                    <Tooltip title="削除">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => onDeleteClick(checkSheet, e)}
                                        sx={{
                                          ml: 1,
                                          color: '#9C27B0',
                                          '&:hover': {
                                            backgroundColor: alpha('#9C27B0', 0.1),
                                            color: '#d32f2f'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>

                                {/* 面接官情報 */}
                                {checkSheet.interviewer_info && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <SupervisorAccountIcon sx={{ fontSize: 16, mr: 0.5, color: '#9C27B0' }} />
                                    <Typography variant="body2" sx={{ color: '#7B1FA2', fontSize: '0.85rem' }}>
                                      {checkSheet.interviewer_info.name} ({checkSheet.interviewer_info.department})
                                    </Typography>
                                  </Box>
                                )}

                                {/* 評価情報 */}
                                {checkSheet.evaluation && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                                    <StarIcon sx={{ fontSize: 16, color: '#FFC107' }} />
                                    <Typography variant="body2" sx={{ color: '#7B1FA2', fontWeight: 600 }}>
                                      評価: {checkSheet.evaluation.overall_score}/100
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </AnimatePresence>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              過去の一次面接チェックシートがありません
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* 履歴確認ダイアログ */}
      <Dialog
        open={historyDialogOpen}
        onClose={handleCloseHistoryDialog}
        maxWidth="md"
        fullWidth
      >
        {/* ヘッダー */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
            color: 'white',
            px: 3,
            py: 3
          }}
        >
          <Typography variant="h5" sx={{ fontWeight: 700, mb: 2 }}>
            過去の面接チェックシート履歴
          </Typography>

          {/* 検索バー */}
          <TextField
            fullWidth
            placeholder="候補者名、職種、キーワードで検索..."
            size="small"
            value={historySearchQuery}
            onChange={(e) => setHistorySearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.25)
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.8),
                  opacity: 1
                },
                '& input': {
                  color: 'white'
                }
              }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle, position: 'relative' }}>
          {loading || parentLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : error || parentError ? (
            <Typography color="error" sx={{ p: 2 }}>
              {error || parentError}
            </Typography>
          ) : selectedCheckSheet ? (
            <Box sx={{ p: 2 }}>
              <Paper elevation={2} sx={{ p: 3, mb: 3 }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography variant="h6" gutterBottom>
                      {selectedCheckSheet.position || '職種未設定'}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      面接日: {new Date(selectedCheckSheet.interview_date).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      面接プロセス: {selectedCheckSheet.interview_stage} ({selectedCheckSheet.interview_stage_order}/{selectedCheckSheet.total_stages}次)
                    </Typography>
                  </Grid>
                  
                  {/* 候補者情報 */}
                  {selectedCheckSheet.candidate_info && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <PersonIcon sx={{ mr: 1, color: 'primary.main' }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          候補者情報
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 4 }}>
                        <Typography>
                          氏名: {selectedCheckSheet.candidate_info.name || '未設定'}
                        </Typography>
                        {selectedCheckSheet.candidate_info.current_company && (
                          <Typography>
                            現職: {selectedCheckSheet.candidate_info.current_position} @ {selectedCheckSheet.candidate_info.current_company}
                          </Typography>
                        )}
                      </Box>
                    </Grid>
                  )}
                  
                  {/* 面接官情報 */}
                  {selectedCheckSheet.interviewer_info && (
                    <Grid item xs={12} md={6}>
                      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
                        <WorkIcon sx={{ mr: 1, color: 'secondary.main' }} />
                        <Typography variant="subtitle1" fontWeight="bold">
                          面接官情報
                        </Typography>
                      </Box>
                      <Box sx={{ pl: 4 }}>
                        <Typography>
                          氏名: {selectedCheckSheet.interviewer_info.name || '未設定'}
                        </Typography>
                        <Typography>
                          部署: {selectedCheckSheet.interviewer_info.department || '未設定'}
                        </Typography>
                        <Typography>
                          役職: {selectedCheckSheet.interviewer_info.position || '未設定'}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Paper>

              {/* 評価結果 */}
              {selectedCheckSheet.evaluation && (
                <Paper elevation={2} sx={{ p: 3 }}>
                  <Typography variant="h6" gutterBottom sx={{ display: 'flex', alignItems: 'center' }}>
                    <StarIcon sx={{ mr: 1, color: 'warning.main' }} />
                    評価結果
                  </Typography>
                  
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="subtitle1" gutterBottom>
                      総合評価: {selectedCheckSheet.evaluation.overall_score}/100
                    </Typography>
                    <Rating 
                      value={selectedCheckSheet.evaluation.overall_score / 20} 
                      precision={0.5} 
                      readOnly 
                    />
                  </Box>

                  {/* カテゴリ別スコア */}
                  {selectedCheckSheet.evaluation.category_scores && (
                    <Grid container spacing={2}>
                      {Object.entries(selectedCheckSheet.evaluation.category_scores).map(([category, data]) => (
                        <Grid item xs={12} md={4} key={category}>
                          <Paper elevation={1} sx={{ p: 2 }}>
                            <Typography variant="subtitle2" gutterBottom>
                              {category}
                            </Typography>
                            <Rating value={data.score} readOnly size="small" />
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                              {data.details}
                            </Typography>
                          </Paper>
                        </Grid>
                      ))}
                    </Grid>
                  )}

                  {/* 強み・弱み */}
                  <Grid container spacing={2} sx={{ mt: 2 }}>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom color="success.main">
                        強み
                      </Typography>
                      <List dense>
                        {selectedCheckSheet.evaluation.strengths.map((strength, index) => (
                          <ListItem key={index}>
                            <Typography variant="body2">• {strength}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                    <Grid item xs={12} md={6}>
                      <Typography variant="subtitle1" gutterBottom color="error.main">
                        改善点
                      </Typography>
                      <List dense>
                        {selectedCheckSheet.evaluation.weaknesses.map((weakness, index) => (
                          <ListItem key={index}>
                            <Typography variant="body2">• {weakness}</Typography>
                          </ListItem>
                        ))}
                      </List>
                    </Grid>
                  </Grid>
                </Paper>
              )}

              {/* アクションボタン */}
              <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end', gap: 2 }}>
                <Button
                  variant="outlined"
                  onClick={handleCloseHistoryDialog}
                  sx={{ borderRadius: '20px' }}
                >
                  キャンセル
                </Button>
                <Button
                  variant="contained"
                  onClick={() => handleContinueInterview(selectedCheckSheet)}
                  sx={{
                    borderRadius: '20px',
                    background: 'linear-gradient(45deg, #4caf50 0%, #45a049 100%)',
                    color: 'white',
                    '&:hover': {
                      background: 'linear-gradient(45deg, #45a049 0%, #3d8b40 100%)',
                    }
                  }}
                >
                  このチェックシートを表示
                </Button>
              </Box>
            </Box>
          ) : historyData && Object.keys(historyData).length > 0 ? (
            <Box sx={{ position: 'relative' }}>
              {/* サマリー情報 */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  mb: 3,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                  borderRadius: 3,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                }}
              >
                <PersonIcon sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    面接チェックシート履歴サマリー
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    全 {Object.keys(historyData).length} 候補者 • 完了済みのチェックシートのみ表示
                  </Typography>
                </Box>
              </Box>

              {/* Timeline コンテナ */}
              <Box sx={{ position: 'relative', pl: 6 }}>
                {/* Timeline 垂直線 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 23,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: 'linear-gradient(180deg, #9C27B0 0%, #BA68C8 100%)',
                    borderRadius: 2,
                    opacity: 0.3
                  }}
                />

                {/* Timeline アイテム */}
                <AnimatePresence>
                  {Object.entries(historyData)
                    .filter(([candidateName, checkSheets]) => {
                      if (!historySearchQuery.trim()) return true;
                      const query = historySearchQuery.toLowerCase();
                      const nameMatch = candidateName.toLowerCase().includes(query);
                      const sheetMatch = checkSheets.some(sheet =>
                        (sheet.interview_stage || '').toLowerCase().includes(query) ||
                        (sheet.position || '').toLowerCase().includes(query) ||
                        (sheet.candidate_name || '').toLowerCase().includes(query)
                      );
                      return nameMatch || sheetMatch;
                    })
                    .map(([candidateName, checkSheets], groupIndex) => (
                      <Box
                        key={candidateName}
                        component={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{
                          duration: 0.4,
                          delay: groupIndex * 0.1,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                        sx={{ position: 'relative', mb: 3 }}
                      >
                        {/* Timeline アイコン */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -35,
                            top: 8,
                            width: 46,
                            height: 46,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)',
                            zIndex: 1,
                            border: `3px solid ${border(theme).light}`
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Box>

                        {/* Timeline カード */}
                        <Box
                          component={motion.div}
                          whileHover={{
                            y: -4,
                            boxShadow: '0 8px 24px rgba(156, 39, 176, 0.2)'
                          }}
                          transition={{ duration: 0.2 }}
                          sx={{
                            p: 3,
                            backgroundColor: bg(theme).card,
                            borderRadius: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: `1px solid ${border(theme).default}`
                          }}
                        >
                          {/* カードヘッダー */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                                {candidateName}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  size="small"
                                  label={`${checkSheets.length}件`}
                                  sx={{
                                    background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                                <CandidateFinalResult checkSheets={checkSheets} />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {checkSheets.map((sheet) => (
                                <Chip
                                  key={sheet.id}
                                  label={sheet.interview_stage || `${sheet.interview_stage_order || 1}次面接`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    borderColor: sheet.is_final_stage ? '#f44336' : '#9C27B0',
                                    color: sheet.is_final_stage ? '#f44336' : '#9C27B0'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>

                          {/* チェックシートリスト */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {checkSheets.map((checkSheet) => (
                              <Box
                                key={checkSheet.id}
                                component={motion.div}
                                whileHover={{ x: 4 }}
                                transition={{ duration: 0.2 }}
                                onClick={() => handleSelectCheckSheet(checkSheet)}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #f3e5f7 0%, #fce4ec 100%)',
                                  border: '1px solid #e1bee7',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    border: '1px solid #9C27B0',
                                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
                                      {checkSheet.interview_stage || `${checkSheet.interview_stage_order || 1}次面接`}
                                    </Typography>
                                    <StatusChips checkSheet={checkSheet} />
                                  </Box>
                                  {onDeleteClick && (
                                    <Tooltip title="削除">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => onDeleteClick(checkSheet, e)}
                                        sx={{
                                          color: '#9C27B0',
                                          '&:hover': {
                                            backgroundColor: alpha('#9C27B0', 0.1),
                                            color: '#d32f2f'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary' }}>
                                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    {new Date(checkSheet.created_at).toLocaleDateString('ja-JP', {
                                      year: 'numeric',
                                      month: 'long',
                                      day: 'numeric',
                                      timeZone: 'Asia/Tokyo'
                                    })}
                                  </Typography>
                                </Box>
                                {checkSheet.evaluation && checkSheet.evaluation.overall_score && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                                    <StarIcon sx={{ fontSize: 16, color: '#FFC107' }} />
                                    <Typography variant="body2" sx={{ color: '#7B1FA2', fontWeight: 600 }}>
                                      評価: {checkSheet.evaluation.overall_score}/100
                                    </Typography>
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </AnimatePresence>
              </Box>
            </Box>
          ) : (
            <Typography sx={{ p: 2, textAlign: 'center', color: 'text.secondary' }}>
              完了済みの面接チェックシートがありません
            </Typography>
          )}
        </DialogContent>
      </Dialog>

      {/* 子アカウント管理ダイアログ */}
      <Dialog
        open={subAccountDialogOpen}
        onClose={handleCloseSubAccountDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">子アカウント管理</Typography>
            <Button
              variant="contained"
              startIcon={<PersonAddIcon />}
              onClick={() => setSubAccountCreateDialogOpen(true)}
              sx={{ borderRadius: '20px' }}
            >
              新規作成
            </Button>
          </Box>
        </DialogTitle>
        <DialogContent>
          {subAccountLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : subAccountError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {subAccountError}
            </Alert>
          ) : (
            <TableContainer component={Paper}>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>メールアドレス</TableCell>
                    <TableCell>権限</TableCell>
                    <TableCell>状態</TableCell>
                    <TableCell>作成日</TableCell>
                    <TableCell align="center">操作</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {subAccounts.length === 0 ? (
                    <TableRow>
                      <TableCell colSpan={5} align="center">
                        子アカウントがありません
                      </TableCell>
                    </TableRow>
                  ) : (
                    subAccounts.map((subAccount) => (
                      <TableRow key={subAccount.id}>
                        <TableCell>{subAccount.email}</TableCell>
                        <TableCell>
                          <Chip 
                            label="面接専用" 
                            size="small" 
                            color="primary" 
                            variant="outlined"
                          />
                        </TableCell>
                        <TableCell>
                          <Chip 
                            label={subAccount.locked ? "ロック中" : "アクティブ"} 
                            size="small" 
                            color={subAccount.locked ? "error" : "success"}
                          />
                        </TableCell>
                        <TableCell>
                          {new Date(subAccount.created_at).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                        </TableCell>
                        <TableCell align="center">
                          <Tooltip title={subAccount.locked ? "ロック解除" : "ロック"}>
                            <IconButton
                              onClick={() => handleToggleSubAccountLock(subAccount)}
                              color={subAccount.locked ? "success" : "warning"}
                            >
                              {subAccount.locked ? <VisibilityIcon /> : <VisibilityOffIcon />}
                            </IconButton>
                          </Tooltip>
                          <Tooltip title="削除">
                            <IconButton
                              onClick={() => handleDeleteSubAccount(subAccount.id)}
                              color="error"
                            >
                              <DeleteIcon />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </DialogContent>
      </Dialog>

      {/* 子アカウント作成ダイアログ */}
      <Dialog
        open={subAccountCreateDialogOpen}
        onClose={() => setSubAccountCreateDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>新しい子アカウントを作成</DialogTitle>
        <DialogContent>
          <Box sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="メールアドレス"
              type="email"
              value={newSubAccount.email}
              onChange={(e) => setNewSubAccount({ ...newSubAccount, email: e.target.value })}
              margin="normal"
              required
            />
            <TextField
              fullWidth
              label="パスワード"
              type={showPassword ? 'text' : 'password'}
              value={newSubAccount.password}
              onChange={(e) => setNewSubAccount({ ...newSubAccount, password: e.target.value })}
              margin="normal"
              required
              InputProps={{
                endAdornment: (
                  <IconButton
                    onClick={() => setShowPassword(!showPassword)}
                    edge="end"
                  >
                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                  </IconButton>
                )
              }}
            />
            <Alert severity="info" sx={{ mt: 2 }}>
              作成される子アカウントは面接サポート機能のみ利用可能です。
            </Alert>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setSubAccountCreateDialogOpen(false)}>
            キャンセル
          </Button>
          <Button 
            onClick={handleCreateSubAccount} 
            variant="contained"
            disabled={!newSubAccount.email || !newSubAccount.password}
          >
            作成
          </Button>
        </DialogActions>
      </Dialog>

      {/* 利用可能なチェックシート一覧ダイアログ */}
      <Dialog
        open={availableCheckSheetsDialogOpen}
        onClose={handleCloseAvailableCheckSheetsDialog}
        maxWidth="lg"
        fullWidth
      >
        {/* ヘッダー */}
        <Box
          sx={{
            background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
            color: 'white',
            px: 3,
            py: 3
          }}
        >
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h5" sx={{ fontWeight: 700 }}>
              面接未実施チェックシート
            </Typography>
            <Button
              variant="outlined"
              onClick={fetchAvailableCheckSheets}
              disabled={availableCheckSheetsLoading}
              sx={{
                color: 'white',
                borderColor: 'white',
                '&:hover': {
                  borderColor: 'white',
                  backgroundColor: alpha('#ffffff', 0.1)
                }
              }}
            >
              {availableCheckSheetsLoading ? '更新中...' : '更新'}
            </Button>
          </Box>

          {/* 検索バー */}
          <TextField
            fullWidth
            placeholder="候補者名、職種、面接段階で検索..."
            size="small"
            value={availableCheckSheetsSearchQuery}
            onChange={(e) => setAvailableCheckSheetsSearchQuery(e.target.value)}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'white' }} />
                </InputAdornment>
              ),
              sx: {
                backgroundColor: alpha('#ffffff', 0.2),
                borderRadius: 3,
                color: 'white',
                '& .MuiOutlinedInput-notchedOutline': {
                  border: 'none'
                },
                '&:hover': {
                  backgroundColor: alpha('#ffffff', 0.25)
                },
                '& input::placeholder': {
                  color: alpha('#ffffff', 0.8),
                  opacity: 1
                },
                '& input': {
                  color: 'white'
                }
              }
            }}
          />
        </Box>

        <DialogContent sx={{ p: 3, backgroundColor: bg(theme).subtle, position: 'relative' }}>
          {availableCheckSheetsLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
              <CircularProgress />
            </Box>
          ) : availableCheckSheetsError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {availableCheckSheetsError}
            </Alert>
          ) : Object.keys(availableCheckSheets).length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography variant="h6" color="text.secondary">
                面接未実施のチェックシートがありません
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                すべてのチェックシートが面接済みです
              </Typography>
            </Box>
          ) : (
            <Box sx={{ position: 'relative' }}>
              {/* サマリー情報 */}
              <Box
                component={motion.div}
                initial={{ opacity: 0, y: -20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
                sx={{
                  mb: 3,
                  p: 2.5,
                  background: 'linear-gradient(135deg, #9C27B0 0%, #BA68C8 100%)',
                  borderRadius: 3,
                  color: 'white',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 2,
                  boxShadow: '0 4px 12px rgba(156, 39, 176, 0.3)'
                }}
              >
                <PersonIcon sx={{ fontSize: 32 }} />
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 700, mb: 0.5 }}>
                    未実施チェックシートサマリー
                  </Typography>
                  <Typography variant="body2" sx={{ opacity: 0.95 }}>
                    全 {Object.keys(availableCheckSheets).length} 候補者 • 面接未実施のみ表示
                  </Typography>
                </Box>
              </Box>

              {/* Timeline コンテナ */}
              <Box sx={{ position: 'relative', pl: 6 }}>
                {/* Timeline 垂直線 */}
                <Box
                  sx={{
                    position: 'absolute',
                    left: 23,
                    top: 0,
                    bottom: 0,
                    width: 3,
                    background: 'linear-gradient(180deg, #9C27B0 0%, #BA68C8 100%)',
                    borderRadius: 2,
                    opacity: 0.3
                  }}
                />

                {/* Timeline アイテム */}
                <AnimatePresence>
                  {Object.entries(availableCheckSheets)
                    .filter(([candidateKey, checkSheets]) => {
                      if (!availableCheckSheetsSearchQuery.trim()) return true;
                      const query = availableCheckSheetsSearchQuery.toLowerCase();
                      const nameMatch = candidateKey.toLowerCase().includes(query);
                      const sheetMatch = checkSheets.some(sheet =>
                        (sheet.interview_stage || '').toLowerCase().includes(query) ||
                        (sheet.candidate_name || '').toLowerCase().includes(query) ||
                        (sheet.position || '').toLowerCase().includes(query)
                      );
                      return nameMatch || sheetMatch;
                    })
                    .map(([candidateKey, checkSheets], groupIndex) => (
                      <Box
                        key={candidateKey}
                        component={motion.div}
                        initial={{ opacity: 0, x: -20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: 20 }}
                        transition={{
                          duration: 0.4,
                          delay: groupIndex * 0.1,
                          ease: [0.4, 0, 0.2, 1]
                        }}
                        sx={{ position: 'relative', mb: 3 }}
                      >
                        {/* Timeline アイコン */}
                        <Box
                          sx={{
                            position: 'absolute',
                            left: -35,
                            top: 8,
                            width: 46,
                            height: 46,
                            borderRadius: '50%',
                            background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            color: 'white',
                            boxShadow: '0 4px 12px rgba(156, 39, 176, 0.4)',
                            zIndex: 1,
                            border: `3px solid ${border(theme).light}`
                          }}
                        >
                          <PersonIcon fontSize="small" />
                        </Box>

                        {/* Timeline カード */}
                        <Box
                          component={motion.div}
                          whileHover={{
                            y: -4,
                            boxShadow: '0 8px 24px rgba(156, 39, 176, 0.2)'
                          }}
                          transition={{ duration: 0.2 }}
                          sx={{
                            p: 3,
                            backgroundColor: bg(theme).card,
                            borderRadius: 3,
                            boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
                            border: `1px solid ${border(theme).default}`
                          }}
                        >
                          {/* カードヘッダー */}
                          <Box sx={{ mb: 2 }}>
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                              <Typography variant="h6" sx={{ fontWeight: 700, color: '#9C27B0' }}>
                                {candidateKey}
                              </Typography>
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <Chip
                                  size="small"
                                  label={`${checkSheets.length}件`}
                                  sx={{
                                    background: 'linear-gradient(135deg, #9C27B0, #BA68C8)',
                                    color: 'white',
                                    fontWeight: 600
                                  }}
                                />
                                <CandidateFinalResult checkSheets={checkSheets} />
                              </Box>
                            </Box>
                            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                              {checkSheets.map((sheet) => (
                                <Chip
                                  key={sheet.id}
                                  label={sheet.interview_stage || `${sheet.interview_stage_order || 1}次面接`}
                                  size="small"
                                  variant="outlined"
                                  sx={{
                                    fontSize: '0.7rem',
                                    borderColor: sheet.is_final_stage ? '#f44336' : '#9C27B0',
                                    color: sheet.is_final_stage ? '#f44336' : '#9C27B0'
                                  }}
                                />
                              ))}
                            </Box>
                          </Box>

                          {/* チェックシートリスト */}
                          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                            {checkSheets.map((checkSheet) => (
                              <Box
                                key={checkSheet.id}
                                component={motion.div}
                                whileHover={{ x: 4 }}
                                transition={{ duration: 0.2 }}
                                onClick={() => handleSelectAvailableCheckSheet(checkSheet)}
                                sx={{
                                  p: 2,
                                  borderRadius: 2,
                                  background: 'linear-gradient(135deg, #f3e5f7 0%, #fce4ec 100%)',
                                  border: '1px solid #e1bee7',
                                  cursor: 'pointer',
                                  '&:hover': {
                                    border: '1px solid #9C27B0',
                                    boxShadow: '0 2px 8px rgba(156, 39, 176, 0.15)'
                                  }
                                }}
                              >
                                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flex: 1 }}>
                                    <Typography variant="subtitle2" sx={{ fontWeight: 700, color: '#7B1FA2' }}>
                                      {checkSheet.interview_stage || '1次面接'}
                                    </Typography>
                                    <InterviewStageChip
                                      stage={checkSheet.interview_stage}
                                      stageOrder={checkSheet.interview_stage_order}
                                      totalStages={checkSheet.total_stages}
                                      isFinal={checkSheet.is_final_stage}
                                    />
                                    <StatusChips checkSheet={checkSheet} />
                                  </Box>
                                  {onDeleteClick && (
                                    <Tooltip title="削除">
                                      <IconButton
                                        size="small"
                                        onClick={(e) => onDeleteClick(checkSheet, e)}
                                        sx={{
                                          color: '#9C27B0',
                                          '&:hover': {
                                            backgroundColor: alpha('#9C27B0', 0.1),
                                            color: '#d32f2f'
                                          }
                                        }}
                                      >
                                        <DeleteIcon fontSize="small" />
                                      </IconButton>
                                    </Tooltip>
                                  )}
                                </Box>

                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: 'text.secondary', mb: 1 }}>
                                  <AccessTimeIcon sx={{ fontSize: 14 }} />
                                  <Typography variant="body2" sx={{ fontSize: '0.8rem' }}>
                                    作成日: {new Date(checkSheet.created_at).toLocaleDateString('ja-JP', { timeZone: 'Asia/Tokyo' })}
                                  </Typography>
                                </Box>

                                {checkSheet.interviewer_info && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                                    <SupervisorAccountIcon sx={{ fontSize: 16, mr: 0.5, color: '#9C27B0' }} />
                                    <Typography variant="body2" sx={{ color: '#7B1FA2', fontSize: '0.85rem' }}>
                                      面接官: {checkSheet.interviewer_info.name}
                                    </Typography>
                                  </Box>
                                )}

                                {checkSheet.evaluation && checkSheet.evaluation.overall_score && (
                                  <Box sx={{ display: 'flex', alignItems: 'center', mt: 1, gap: 1 }}>
                                    <StarIcon sx={{ fontSize: 16, color: '#FFC107' }} />
                                    <Typography variant="body2" sx={{ color: '#7B1FA2', fontWeight: 600 }}>
                                      評価: {checkSheet.evaluation.overall_score}/100
                                    </Typography>
                                  </Box>
                                )}

                                {checkSheet.decision_status && (
                                  <Box sx={{ mt: 1 }}>
                                    <Chip
                                      label={checkSheet.decision_status === 'hired' ? '採用決定' : checkSheet.decision_status === 'rejected' ? '不採用' : '判定待ち'}
                                      color={checkSheet.decision_status === 'hired' ? 'success' : checkSheet.decision_status === 'rejected' ? 'error' : 'default'}
                                      size="small"
                                      icon={checkSheet.decision_status === 'hired' ? <WorkIcon /> : checkSheet.decision_status === 'rejected' ? <BlockIcon /> : undefined}
                                    />
                                  </Box>
                                )}
                              </Box>
                            ))}
                          </Box>
                        </Box>
                      </Box>
                    ))}
                </AnimatePresence>
              </Box>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseAvailableCheckSheetsDialog}>
            閉じる
          </Button>
        </DialogActions>
      </Dialog>

      {/* スナックバー */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
      >
        <Alert 
          onClose={() => setSnackbarOpen(false)} 
          severity={snackbarSeverity}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </>
  );
} 